import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  ArrowBack,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@material-ui/icons";
import CardCommon from "../../../../components/card/CardCommon";
import { AxiosError } from "axios";
import { useRouteMatch, useHistory } from "react-router-dom";
import { fetchLogsKeyInfo } from "../../../../services/salesApp/logsService";
import { CustomTheme } from "../../../../types/customTheme";

// Define the route params type
interface RouteParams {
  locationId: string;
}

// Define the order data structure
interface OrderData {
  datetime: string;
  queOrderNo: string;
}

interface OrdersResponse {
  data: {
    [orderId: string]: OrderData;
  };
  count: number;
}

export interface VoidTransactionDetailsModalProps {
  selectedDate: any;
  handleOrderKeyClick: (orderId: string, queOrderNo: string) => void;
  onBack?: () => void;
  showBackButton?: boolean;
  setSelectedData: any;
  isGeneric?: boolean;
}

// Constants - Updated to show "Jun 05, 2026" format
const DISPLAY_DATE_OPTIONS = {
  month: "short",
  day: "2-digit",
  year: "numeric",
} as const;

const GRID_BREAKPOINTS = {
  xs: 12,
  sm: 6,
  md: 4,
  lg: 2,
} as const;

const CARD_HEIGHT = "90px";
const MARGIN_BOTTOM = "20px";

// Styles for search textfield
const useStyles = makeStyles((theme: CustomTheme) => ({
  textFieldOpen: {
    overflowWrap: "break-word",
    wordWrap: "break-word",
    borderRadius: "10px",
    border: "none",
    maxWidth: "160px",
    backgroundColor: theme.palette.background.entity_background,
    [`& fieldset`]: {
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  searchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "16px",
  },
  searchBoxWrapper: {
    flex: 1,
    display: "flex",
    justifyContent: "flex-start",
  },
}));

// Helper functions
const formatDateToYYYYMMDD = (date: any): string => {
  if (!date) return "";

  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
  }

  if (typeof date === "string") {
    if (/^\d{8}$/.test(date)) {
      return date;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date.replace(/-/g, "");
    }
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    }
  }

  if (typeof date === "number") {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    }
  }

  return "";
};

// Updated display date format: "Jun 05, 2026"
const formatDisplayDate = (date: any): string => {
  if (!date) return "";

  let d: Date;

  // If it's a string in YYYYMMDD format (e.g., "20260608")
  if (typeof date === "string" && /^\d{8}$/.test(date)) {
    const year = parseInt(date.substring(0, 4));
    const month = parseInt(date.substring(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(date.substring(6, 8));
    d = new Date(year, month, day);
  }
  // If it's a Date object
  else if (date instanceof Date) {
    d = date;
  }
  // Try parsing as regular date string
  else {
    d = new Date(date);
  }

  if (isNaN(d.getTime())) return String(date);

  return d.toLocaleDateString("en-US", DISPLAY_DATE_OPTIONS);
};

const formatDateTime = (datetime: string): string => {
  const d = new Date(datetime);
  if (isNaN(d.getTime())) {
    return "Invalid date";
  }
  return d
    .toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(/,/g, "");
};

const LogsKey: React.FC<VoidTransactionDetailsModalProps> = ({
  selectedDate,
  handleOrderKeyClick,
  onBack,
  showBackButton = false,
  setSelectedData,
  isGeneric,
}) => {
  const classes = useStyles();
  const [ordersData, setOrdersData] = useState<OrdersResponse>({
    data: {},
    count: 0,
  });
  const [filteredOrders, setFilteredOrders] = useState<OrdersResponse>({
    data: {},
    count: 0,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const match = useRouteMatch<RouteParams>();
  const history = useHistory();
  const theme: CustomTheme = useTheme();

  const formattedSelectedDate = useMemo(
    () => formatDisplayDate(selectedDate),
    [selectedDate],
  );

  const containerStyle = useMemo(() => ({ marginTop: "12px" }), []);
  const loadingBoxStyle = useMemo(
    () => ({ textAlign: "center" as const, padding: "40px" }),
    [],
  );
  const errorBoxStyle = useMemo(
    () => ({ textAlign: "center" as const, padding: "40px", color: "#f44336" }),
    [],
  );
  const emptyBoxStyle = useMemo(
    () => ({ textAlign: "center" as const, padding: "40px", color: "#999" }),
    [],
  );

  // Clear search handler
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Convert orders object to sorted array
  const getSortedOrdersArray = useCallback(
    (orders: { [key: string]: OrderData }) => {
      // Convert object to array of [orderId, orderData] pairs
      const ordersArray = Object.entries(orders);

      // Sort by datetime (ascending - oldest first)
      const sortedArray = ordersArray.sort((a, b) => {
        const dateA = new Date(a[1].datetime).getTime();
        const dateB = new Date(b[1].datetime).getTime();
        return dateA - dateB; // Ascending order
      });

      // Convert back to object if needed, or return array
      const sortedObject: { [key: string]: OrderData } = {};
      sortedArray.forEach(([orderId, orderData]) => {
        sortedObject[orderId] = orderData;
      });

      return sortedObject;
    },
    [],
  );

  // Filter orders based on search term
  const filterOrders = useCallback(() => {
    if (!searchTerm.trim()) {
      // Sort orders by datetime ascending
      const sortedData = getSortedOrdersArray(ordersData.data);
      setFilteredOrders({
        data: sortedData,
        count: Object.keys(sortedData).length,
      });
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered: { [key: string]: OrderData } = {};

    Object.entries(ordersData.data).forEach(([orderId, orderInfo]) => {
      const orderIdMatch = orderId.toLowerCase().includes(searchLower);
      const queOrderNoMatch = orderInfo.queOrderNo
        .toLowerCase()
        .includes(searchLower);

      if (orderIdMatch || queOrderNoMatch) {
        filtered[orderId] = orderInfo;
      }
    });

    // Sort filtered orders by datetime ascending
    const sortedFiltered = getSortedOrdersArray(filtered);
    setFilteredOrders({
      data: sortedFiltered,
      count: Object.keys(sortedFiltered).length,
    });
  }, [ordersData, searchTerm, getSortedOrdersArray]);

  // Apply filter when ordersData or searchTerm changes
  useEffect(() => {
    filterOrders();
  }, [filterOrders]);

  const fetchOrderKeys = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const formattedDate = formatDateToYYYYMMDD(selectedDate);
      console.log("Fetching order keys for date:", formattedDate);

      if (!formattedDate) {
        throw new Error("Invalid date format");
      }

      const response = await fetchLogsKeyInfo(
        match.params.locationId,
        formattedDate,
      );

      if (response.status === 200 && response.data) {
        const newOrdersData = {
          data: response.data.data || {},
          count: response.data.count || 0,
        };
        setOrdersData(newOrdersData);
        setFilteredOrders(newOrdersData);
        setSearchTerm("");
      } else {
        setOrdersData({ data: {}, count: 0 });
        setFilteredOrders({ data: {}, count: 0 });
        setError(response.data?.message || "No keys found");
      }
    } catch (error) {
      const err = error as AxiosError;
      console.error("Error fetching order keys:", err);
      setOrdersData({ data: {}, count: 0 });
      setFilteredOrders({ data: {}, count: 0 });
      setError(err.message || "Failed to fetch order keys");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, match.params.locationId]);

  const onOrderClick = useCallback(
    (orderId: string, queOrderNo: string, event: React.MouseEvent) => {
      const isCtrlPressed = event.ctrlKey || event.metaKey;

      if (isCtrlPressed) {
        const url = `/${match.params.locationId}/logs/${orderId}`;
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        handleOrderKeyClick(orderId, queOrderNo);
      }
    },
    [handleOrderKeyClick, match.params.locationId],
  );

  useEffect(() => {
    if (selectedDate && match.params.locationId) {
      fetchOrderKeys();
    }
  }, [selectedDate, match.params.locationId, fetchOrderKeys]);

  const orderItems = useMemo(() => {
    return Object.entries(filteredOrders.data).map(
      ([orderId, orderInfo], index) => {
        const formattedDateTime = formatDateTime(orderInfo.datetime);

        return (
          <Grid
            item
            {...GRID_BREAKPOINTS}
            key={`order-${index}-${orderId}`}
            style={{ marginBottom: MARGIN_BOTTOM }}
          >
            <Paper
              elevation={0}
              style={{
                cursor: "pointer",
                height: CARD_HEIGHT,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: theme.palette.background.entity_background,
                borderRadius: "8px",
                borderLeft: "4px solid #1976d2",
                transition: "all 0.3s ease",
                padding: "8px",
              }}
              onClick={(event) => {
                setSelectedData({ data: selectedDate });
                onOrderClick(orderId, orderInfo.queOrderNo, event);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e3f2fd";
                e.currentTarget.style.transform = "translateX(4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme.palette.background.entity_background;
                e.currentTarget.style.transform = "translateX(0)";
              }}
            >
              <Typography
                variant="h6"
                style={{
                  fontWeight: 600,
                  color: "#1976d2",
                  marginBottom: "4px",
                }}
              >
                {orderInfo.queOrderNo}
              </Typography>
              <Typography
                variant="caption"
                style={{
                  color: "#666",
                  fontSize: "10px",
                  marginBottom: "4px",
                }}
              >
                Order: {orderId}
              </Typography>
              <Typography
                variant="caption"
                style={{
                  color: theme.palette.custom.orange.contrastText,
                  fontSize: "10px",
                  fontWeight: 500,
                }}
              >
                {formattedDateTime}
              </Typography>
            </Paper>
          </Grid>
        );
      },
    );
  }, [
    filteredOrders.data,
    onOrderClick,
    theme.palette.background.entity_background,
    theme.palette.custom.orange.contrastText,
  ]);

  return (
    <Grid container spacing={2} style={containerStyle}>
      <Grid item xs={12}>
        <CardCommon>
          <Box style={{ padding: "16px" }}>
            {/* Header with Title and Search Box */}
            {ordersData.count > 0 && (
              <Box className={classes.searchContainer}>
                {/* Styled Search Box */}
                <Box className={classes.searchBoxWrapper}>
                  <TextField
                    classes={{ root: classes.textFieldOpen }}
                    id="search-orders"
                    placeholder="Search"
                    variant="outlined"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    value={searchTerm}
                    style={{
                      height: "40px",
                      marginTop: "4px",
                      marginBottom: "4px",
                    }}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search"
                            edge="end"
                            onClick={handleClearSearch}
                            size="small"
                          >
                            {searchTerm ? <ClearIcon /> : <SearchIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  Orders for {formattedSelectedDate}
                </Typography>
              </Box>
            )}

            {loading && (
              <Box style={loadingBoxStyle}>
                <CircularProgress />
                <Typography variant="body2" style={{ marginTop: "8px" }}>
                  Loading orders...
                </Typography>
              </Box>
            )}

            {error && !loading && (
              <Box style={errorBoxStyle}>
                <Typography variant="body1">{error}</Typography>
              </Box>
            )}

            {!loading && !error && filteredOrders.count === 0 && searchTerm && (
              <Box style={emptyBoxStyle}>
                <Typography variant="body1">
                  No orders found matching "{searchTerm}"
                </Typography>
              </Box>
            )}

            {!loading &&
              !error &&
              filteredOrders.count === 0 &&
              !isGeneric &&
              !searchTerm && (
                <Box style={emptyBoxStyle}>
                  <Typography variant="body1">
                    No orders found for this date
                  </Typography>
                </Box>
              )}

            {!loading && !error && filteredOrders.count > 0 && (
              <>
                <Grid container spacing={2}>
                  {orderItems}
                </Grid>

                <CardCommon
                  backgroundColor={"entity_background"}
                  style={{
                    height: "50px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "16px",
                  }}
                >
                  <Typography variant="subtitle2">
                    Total Orders: {filteredOrders.count}
                  </Typography>
                </CardCommon>
              </>
            )}
          </Box>
        </CardCommon>
      </Grid>
    </Grid>
  );
};

LogsKey.displayName = "LogsKey";

export default LogsKey;
